export type HealthState = 'healthy' | 'starting' | 'recovering' | 'degraded' | 'offline' | 'disabled' | 'unknown';

export type RecoveryAction = {
  id: string;
  label: string;
  method: 'POST';
  endpoint: string;
};

export type SystemModule = {
  id: string;
  label: string;
  healthState: string;
  description: string;
};

export type OperationalIncident = {
  id: string;
  subsystem: string;
  state: string;
  severity: string;
  summary: string;
  details: unknown;
  occurrence_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  resolved_at: string | null;
};

export type OperationsStatus = {
  available: boolean;
  updatedAt: string | null;
  release: {
    version: string | null;
    commit: string | null;
    branch: string | null;
    clean: boolean | null;
  } | null;
  backup: {
    lastAttemptAt: string | null;
    lastAttemptResult: 'pass' | 'fail' | null;
    lastSuccessfulAt: string | null;
    reason: string | null;
    version: string | null;
    commit: string | null;
    directoryName: string | null;
    verified: boolean | null;
  } | null;
  deployment: {
    result: 'pass' | 'rollback' | 'fail' | null;
    completedAt: string | null;
    fromVersion: string | null;
    toVersion: string | null;
    fromCommit: string | null;
    toCommit: string | null;
    rollbackVersion: string | null;
    rollbackCommit: string | null;
  } | null;
  recovery: {
    ready: boolean | null;
    rollbackVersion: string | null;
    rollbackCommit: string | null;
    backupAt: string | null;
  } | null;
};

export type SystemHealthSnapshot = {
  overall: HealthState;
  subsystems: Array<{
    id: string;
    label: string;
    state: HealthState;
    required: boolean;
    detail: string;
    lastError: string | null;
    recoveryAction: RecoveryAction | null;
  }>;
  attention: Array<{
    subsystem: string;
    label: string;
    state: HealthState;
    message: string;
    action: RecoveryAction | null;
  }>;
  incidents: OperationalIncident[];
  streamSetup: {
    readiness: 'ready' | 'degraded-usable' | 'checking' | 'not-ready';
    selectedStyle: string;
    modes: {
      rehearsalEnabled: boolean;
      safeModeEnabled: boolean;
      suppressedFeatures: string[];
    };
    alerts: {
      enabled: boolean;
      overlayClients: number | null;
      queued: number;
      playing: number;
      failed: number;
      simulatedFailed: number;
    };
    wam: {
      status: string;
      current: { category?: string; prompt?: string } | null;
    };
  };
  modules: SystemModule[];
  moduleCatalogError: string | null;
  operations: OperationsStatus | null;
  operationsError: string | null;
};

type HealthOverview = Omit<SystemHealthSnapshot, 'modules' | 'moduleCatalogError' | 'operations' | 'operationsError'>;
type FoundationCatalog = { modules?: Array<Record<string, unknown>> };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  return (await response.json().catch(() => ({}))) as T;
}

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function mapModule(item: Record<string, unknown>, index: number): SystemModule {
  const id = text(item.id, `module-${index + 1}`);
  return {
    id,
    label: text(item.label, id),
    healthState: text(item.health_state, 'unknown'),
    description: text(item.description, text(item.detail, 'Registered FFSControl capability.')),
  };
}

export async function loadSystemHealth(): Promise<SystemHealthSnapshot> {
  const [overview, catalogResult, operationsResult] = await Promise.all([
    request<HealthOverview>('/api/v1/foundation/overview'),
    request<FoundationCatalog>('/api/v1/foundation/catalog')
      .then((catalog) => ({ catalog, error: null as string | null }))
      .catch((error: unknown) => ({
        catalog: { modules: [] } satisfies FoundationCatalog,
        error: error instanceof Error ? error.message : 'Registered capabilities could not be loaded.',
      })),
    request<OperationsStatus>('/api/v1/system/operations')
      .then((operations) => ({ operations, error: null as string | null }))
      .catch((error: unknown) => ({
        operations: null,
        error: error instanceof Error ? error.message : 'Deployment and recovery status could not be loaded.',
      })),
  ]);

  return {
    ...overview,
    modules: (catalogResult.catalog.modules ?? []).map(mapModule),
    moduleCatalogError: catalogResult.error,
    operations: operationsResult.operations,
    operationsError: operationsResult.error,
  };
}

export function runRecoveryAction(action: RecoveryAction): Promise<unknown> {
  return request(action.endpoint, { method: 'POST' });
}
